import PEST_ICON from "@/assets/img/subcategory/bugs.svg";
import OWNER_ICON_HOTEL_OPERATIONS from "@/assets/img/owner/hotel-operations.svg";
import OWNER_ICON_GM_COMMERCIAL from "@/assets/img/owner/gm-commercial.svg";
import OWNER_ICON_ENGINEERING from "@/assets/img/owner/engineering.svg";
import OWNER_ICON_OWNER_CAPEX_REVIEW from "@/assets/img/owner/owner-capex-review.svg";
import OWNER_ICON_EXTERNAL_CONSTRAINT from "@/assets/img/owner/external-constraint.svg";
import OWNER_ICON_RISK_COMPLIANCE from "@/assets/img/owner/risk-compliance.svg";
import OWNER_ICON_UNCLASSIFIED from "@/assets/img/owner/unclassified.svg";
import { internalLogo } from "@/utils/otaLogo";

export const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Full descriptive portfolio name for the executive page's main heading (2026-07-20).
// portfolio_label from the API is just the short code ("Marriott"); there's no backend
// field yet for a richer display name, so known portfolios are curated here by hand.
// Falls back to "{portfolio_label} Portfolio" for anything not in this list.
// 和本地竞品组的差值 |gap| ≤ 0.2 视为持平（2026-10-01）：不标红绿、不计入 Below benchmark、
// 不算 Largest shortfall / lead / Weakest category。各张竞品对比表共用这一个阈值
export const COMP_GAP_FLAG_THRESHOLD = 0.2;

// 大类名在窄列里的简称（Weakest category 等），没列出的用原名
export const CATEGORY_SHORT_LABELS = {
  "Location & Neighbourhood": "Location",
  "Hotel Amenities & Atmosphere": "Amenities",
  "Food & Beverage": "F&B",
  "Guest Experience & Service": "Service",
  "Value for Money": "Value",
};

// 按 ±COMP_GAP_FLAG_THRESHOLD 给差值上色：领先 is-ahead / 落后 is-behind / 持平 is-par（灰）
export function compGapClass(value) {
  if (value == null) return "";
  const rounded = Math.round(value * 10) / 10;
  if (rounded > COMP_GAP_FLAG_THRESHOLD) return "is-ahead";
  if (rounded < -COMP_GAP_FLAG_THRESHOLD) return "is-behind";
  return "is-par";
}

export const PORTFOLIO_DISPLAY_NAMES = {
  Marriott: "Marriott Luxury Portfolio",
};

// Authoritative labelEn values from the L1 classifier's failure-type registry, used by
// labelForFailureType as a fallback when a failure_type isn't already labeled by this
// period's live findings (failureTypeLabelMap) — e.g. failure types that only appear in
// mock/demo cases and never in a real finding summary.
export const FAILURE_TYPE_LABEL_OVERRIDES = {
  bed_bug_presence: "Bed bug",
  data_security_incident: "Data security incident during stay",
  emergency_response_failure: "Inadequate emergency response",
  foodborne_illness: "Foodborne illness attributed to hotel food",
  perceived_discriminatory_treatment: "Guest-reported discriminatory treatment",
  physical_safety_incident: "Physical safety incident",
  theft_or_property_loss: "Theft or property loss",
};
// failure_type -> icon, checked ahead of the subCategory-derived lookup in
// subCategoryIcon() below (case cards only carry failure_type, not subCategory).
export const FAILURE_TYPE_ICON_OVERRIDES = {
  pest_presence: PEST_ICON,
  bed_bug_presence: PEST_ICON,
};
// failureTypeDuty key ("hotel_operations", "gm_commercial", ...) -> its icon
// in src/assets/img/owner/, for the "Signals this month" owner filter dropdown.
export const DUTY_ICONS = {
  hotel_operations: OWNER_ICON_HOTEL_OPERATIONS,
  gm_commercial: OWNER_ICON_GM_COMMERCIAL,
  engineering: OWNER_ICON_ENGINEERING,
  owner_capex_review: OWNER_ICON_OWNER_CAPEX_REVIEW,
  external_constraint: OWNER_ICON_EXTERNAL_CONSTRAINT,
  risk_compliance: OWNER_ICON_RISK_COMPLIANCE,
  // failure_type 在 failureTypeDuty 里查不到部门时归到这里(见 index.vue 的 || "unclassified")。
  unclassified: OWNER_ICON_UNCLASSIFIED,
};
// Canonical brand display name overrides — the backend doesn't consistently
// spell every brand the same way across its own data sources (brand_tier_full's
// brand_display_name says "Ritz-Carlton"; the brands dict's key-derived name
// says "The Ritz-Carlton"), which silently broke name-based joins between
// them (see brandMovementRows) and showed the "wrong" (non-canonical) form on
// whichever chart read the shorter version. Keyed by the lowercased, non-
// alphanumeric-stripped form (see fuzzyBrandKey) so either source's spelling
// maps to the one true label.
export const BRAND_NAME_ALIASES = {
  ritzcarlton: "The Ritz-Carlton",
};
// Which country each brand's group-level reports live under (backend keys report
// data by country per brand — mirrors the backend's own countryAndBrands map).
// Add a brand's country here whenever a new market/brand combo ships, otherwise
// its group report/linked-cases/hotels calls silently fall back to "China" below.
export const COUNTRY_BRANDS = {
  "": ["EDITION Hotels"],
  China: ["EDITION Hotels", "Ritz-Carlton", "JW Marriott", "St. Regis", "The Luxury Collection", "W Hotels", "Bvlgari"],
  Thailand: ["InterContinental", "Hotel Indigo", "Kimpton", "Six Senses", "Vignette Collection"],
};
// Reverse lookup, brand -> country, keyed by fuzzyBrandKey so spelling variants
// (e.g. "Ritz-Carlton" vs the canonical "The Ritz-Carlton" from BRAND_NAME_ALIASES)
// resolve to the same entry.
export const BRAND_COUNTRY_BY_KEY = {};
Object.keys(COUNTRY_BRANDS).forEach((country) => {
  if (!country) return;
  COUNTRY_BRANDS[country].forEach((brand) => {
    const key = brand.toLowerCase().replace(/[^a-z0-9]+/g, "");
    BRAND_COUNTRY_BY_KEY[key] = country;
  });
});
Object.entries(BRAND_NAME_ALIASES).forEach(([aliasKey, canonicalName]) => {
  if (BRAND_COUNTRY_BY_KEY[aliasKey] && !BRAND_COUNTRY_BY_KEY[canonicalName.toLowerCase().replace(/[^a-z0-9]+/g, "")]) {
    BRAND_COUNTRY_BY_KEY[canonicalName.toLowerCase().replace(/[^a-z0-9]+/g, "")] = BRAND_COUNTRY_BY_KEY[aliasKey];
  }
});
// 组合标题按「集团 + 国家」配置。原来只要是 IHG 就写死泰国标题，IHG 别的国家的组合也会显示
// "Thailand Luxury …"。后端只给 portfolio_label（集团名），没有组合名称 / 国家字段，国家只能从
// 组合里的品牌推（portfolioCountryOf）。没有配置的组合走通用写法（"{集团} Portfolio" / "{集团} Overview"）。
//   breadcrumb：面包屑一级 + 左侧栏顶部那一项（sidebarKey 是它的 i18n 键）
//   title：一级页大标题
export const PORTFOLIO_TITLES = {
  "IHG|Thailand": {
    breadcrumb: "Thailand Luxury Overview",
    title: "Thailand Luxury & Lifestyle",
    sidebarKey: "sidebar.thailandLuxuryOverview",
  },
};

// 组合所在国家：品牌名带国家后缀（"Kimpton_Thailand"）时直接取后缀，否则查 BRAND_COUNTRY_BY_KEY。
// 所有能认出来的品牌都在同一个国家才返回该国家；认不出、或跨国家，返回 ""（不套国家标题）。
export function portfolioCountryOf(brandNames) {
  const countries = new Set();
  (brandNames || []).forEach((raw) => {
    const name = String(raw || "");
    const parts = name.split("_");
    const suffix = parts.length > 1 ? parts[parts.length - 1] : "";
    if (suffix && COUNTRY_BRANDS[suffix]) {
      countries.add(suffix);
      return;
    }
    const base = parts.length > 1 ? parts.slice(0, -1).join("_") : name;
    const country = BRAND_COUNTRY_BY_KEY[base.toLowerCase().replace(/[^a-z0-9]+/g, "")];
    if (country) countries.add(country);
  });
  return countries.size === 1 ? Array.from(countries)[0] : "";
}

// 这个组合有没有专门配置的标题；group 用 isIhgGroup 这类宽松匹配归一后的集团键（目前只有 "IHG"）
export function portfolioTitleFor(groupKey, country) {
  if (!groupKey || !country) return null;
  return PORTFOLIO_TITLES[`${groupKey}|${country}`] || null;
}

// Property-map camera defaults per country (title label, initial center/zoom, the
// envelope GreaterChinaMap always fits — see that component's own bounds comment — and
// the ISO code whose country / province labels stay visible; every other country's
// label is hidden). Keyed the same as COUNTRY_BRANDS; add an entry here alongside any
// new country there.
// China's envelope is the populated east/centre (Yunnan–Heilongjiang, Hainan–Beijing),
// not the whole country: the map card is wide and short (~3:1), so fitting Xinjiang/Tibet
// too left it at a world-ish zoom — and with renderWorldCopies off, Mapbox then shifted
// the camera west to keep the view inside ±180°, centring it on Central Asia. Pins
// outside this envelope (e.g. Urumqi, Lhasa) still extend it.
export const COUNTRY_MAP_CONFIG = {
  China: { label: "China", center: [104.1954, 35.8617], zoom: 3.5, bounds: [[97, 18], [125, 45]], iso: "CN" },
  Thailand: { label: "Thailand", center: [100.9925, 15.87], zoom: 5, bounds: [[97.3, 5.6], [105.7, 20.5]], iso: "TH" },
};
export const DEFAULT_COUNTRY_MAP_CONFIG = COUNTRY_MAP_CONFIG.China;
export const CONTEXT_GROUP_LABELS = {
  harm_outcome: "Injury reported",
  vulnerable_guest: "Vulnerable guest involved",
  external_escalation: "External escalation raised",
  escalation_channel: "Escalated to HQ/management",
};
export const OUTCOME_TYPE_LABELS = {
  no_return_intent: "Guest indicated no return",
  not_recommend: "Guest would not recommend",
};
export const RECOGNITION_TYPE_LABELS = {
  lost_property_returned: "Lost property returned",
  exceptional_safety_or_emergency_action: "Exceptional safety or emergency action",
  exceptional_service_recovery: "Exceptional service recovery",
  // Seen in /hotel/period-report/brand-linked-cases' positive_highlights —
  // a different (but overlapping) vocabulary from recognition_events above.
  proactive_or_personalised_service: "Proactive or personalised service",
  staff_or_team_recognition: "Staff or team recognition",
  effective_service_recovery: "Effective service recovery",
};
// Specific case_context markers seen in the linked-cases feed. More granular than
// CONTEXT_GROUP_LABELS (which only covers the parent context_group).
export const MARKER_LABELS = {
  injury_reported: "Injury reported",
  child_or_minor_involved: "Child involved",
  pregnancy_explicitly_referenced: "Pregnancy referenced",
  disability_or_mobility_need_involved: "Disability/mobility need involved",
  legal_or_regulatory_escalation: "Police/regulatory report filed",
  hq_complaint_filed: "Escalated to HQ",
};

// Group-level "anchor" evidence cards ("Group key-risk evidence" / "Management
// escalation") — real-data versions (2026-08-07) of what used to be two hand-curated,
// hardcoded objects (dated 2026-07-14) that had drifted out of sync with the live report
// (wrong hotel, wrong failure type, a quote that no longer exists in the current month's
// evidence). Card labels/copy are unchanged; only the hotel/quote/tag are now computed
// from the live groupData each month — see realKeyRiskAnchor/realEscalationAnchor below.
// No reviewId/ref is available for a computed pick (those were hand-assigned short codes),
// so the template no longer renders them.
export const ESCALATION_MARKERS = [
  { re: /12315/, tag: "12315 consumer protection" },
  { re: /consumer protection/i, tag: "Consumer protection authority" },
  { re: /(tourism|culture and tourism)\s+(authority|bureau|department)/i, tag: "Culture & Tourism authority" },
  { re: /(filed|lodged)\s+(a\s+)?(formal\s+)?complaint/i, tag: "Formal complaint" },
  { re: /legal action|lawsuit|\bsued\b/i, tag: "Legal action" },
  { re: /regulator(y)?\s+(authority|body|agency)?/i, tag: "Regulatory authority" },
];

// ?type=mock support: replaces every real brand/hotel name in the group report
// payload with a numbered placeholder (brand1, hotel1, ...) so the page can be
// demoed without exposing real client names. Numbering is assigned in the
// order brands/hotels are first encountered, so the same entity always gets
// the same placeholder everywhere it appears (breadcrumbs, selects, tooltips,
// narrative text, evidence quotes, etc).
export function brandKeyParts(brandKey) {
  const parts = String(brandKey).split("_");
  return parts.length > 1
    ? { name: parts.slice(0, -1).join("_"), suffix: parts[parts.length - 1] }
    : { name: brandKey, suffix: "" };
}

// Known client brands get a stable letter-coded placeholder (e.g. "Brand R")
// instead of a sequential brandN, so the same brand always demos the same way.
export const KNOWN_BRAND_CODES = [
  { test: /ritz/i, code: "R" },
  { test: /st\.?\s*regis/i, code: "S" },
  { test: /jw/i, code: "J" },
  { test: /luxury collection/i, code: "T" },
  { test: /edition/i, code: "E" },
  { test: /b[uv]lgari/i, code: "B" },
  { test: /^w\b/i, code: "W" },
];

export function matchKnownBrandCode(name) {
  const match = KNOWN_BRAND_CODES.find(({ test }) => test.test(name));
  return match ? `Brand ${match.code}` : null;
}

export function buildMockNameMap(rawData) {
  const nameToPlaceholder = {};
  const brandKeyMap = {};
  let brandSeq = 0;
  Object.keys(rawData.brands || {}).forEach((brandKey) => {
    const { name } = brandKeyParts(brandKey);
    if (!nameToPlaceholder[name]) {
      const knownPlaceholder = matchKnownBrandCode(name);
      if (knownPlaceholder) {
        nameToPlaceholder[name] = knownPlaceholder;
      } else {
        brandSeq += 1;
        nameToPlaceholder[name] = `brand${brandSeq}`;
      }
    }
  });
  Object.keys(rawData.brands || {}).forEach((brandKey) => {
    const { name, suffix } = brandKeyParts(brandKey);
    const placeholder = nameToPlaceholder[name];
    brandKeyMap[brandKey] = suffix ? `${placeholder}_${suffix}` : placeholder;
  });

  let hotelSeq = 0;
  const registerHotelName = (hotelName) => {
    if (hotelName && !nameToPlaceholder[hotelName]) {
      hotelSeq += 1;
      nameToPlaceholder[hotelName] = `hotel${hotelSeq}`;
    }
  };
  Object.values(rawData.brands || {}).filter(Boolean).forEach((brand) => {
    Object.values(brand.hotel_map || {}).forEach(registerHotelName);
  });
  Object.values(rawData.hotel_map || {}).forEach(registerHotelName);

  return { nameToPlaceholder, brandKeyMap };
}

export function buildMockNameRegex(names) {
  if (!names.length) return null;
  const escaped = names
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return new RegExp(escaped.join("|"), "g");
}

export function maskStringsDeep(node, regex, nameToPlaceholder) {
  if (Array.isArray(node)) {
    return node.map((item) => maskStringsDeep(item, regex, nameToPlaceholder));
  }
  if (node && typeof node === "object") {
    const result = {};
    Object.keys(node).forEach((key) => {
      result[key] = maskStringsDeep(node[key], regex, nameToPlaceholder);
    });
    return result;
  }
  if (typeof node === "string" && regex) {
    return node.replace(regex, (match) => nameToPlaceholder[match] || match);
  }
  return node;
}

// GET /hotel/brands/group-hotels' competitorAnalysis field (2026-08): an
// array of named local competitors, each carrying its own
// {prevMonth,currMonth} analysis (either can be null if that competitor
// hotel had no scoreable reviews that month) — replaces the older single
// aggregate-object shape ({prevMonth,currMonth} directly on
// competitorAnalysis) every existing comp-set computed in index.vue reads.
// Rather than rewriting every one of those call sites to understand the new
// array, this builds a review-count-weighted aggregate back into the SAME
// {prevMonth,currMonth} shape (so all of that existing code keeps working
// unmodified) while also exposing the raw per-competitor `list` for the two
// things that actually need individual competitors: the Properties table's
// "Rank" column and the "Local Competitive Set" table.
// Called once per hotel at load time (fetchGroupHotels / the mock branch of
// fetchGroupReport) so every consumer downstream just sees the normalized
// shape regardless of whether the data came from the real API or mock.
export function normalizeCompetitorAnalysis(raw) {
  if (!Array.isArray(raw)) {
    // Already-normalized mock data, or an older aggregate-shaped payload —
    // pass through with an empty list rather than guessing.
    return raw && typeof raw === "object" ? { list: [], ...raw } : { list: [], prevMonth: null, currMonth: null };
  }

  const list = raw.map((c) => ({
    id: c.competitorHotelId,
    name: c.competitorHotelName,
    prevMonthReviewCount: c.prevMonthReviewCount || 0,
    currMonthReviewCount: c.currMonthReviewCount || 0,
    prevMonth: (c.analysis && c.analysis.prevMonth) || null,
    currMonth: (c.analysis && c.analysis.currMonth) || null,
  }));

  const buildAggregate = (periodKey) => {
    const entries = list.map((c) => c[periodKey]).filter((a) => a && a.avgScore != null && a.count);
    if (!entries.length) return null;
    const totalCount = entries.reduce((sum, a) => sum + a.count, 0);
    const avgScore = Math.round((entries.reduce((sum, a) => sum + a.avgScore * a.count, 0) / totalCount) * 10) / 10;

    // Weighted merge of every competitor's categoryAvgs (and, one level
    // down, subcategoryAvgs) into a single comp-set-wide category table —
    // same review-count weighting as the overall avgScore above.
    const catAgg = {};
    entries.forEach((a) => {
      Object.entries(a.categoryAvgs || {}).forEach(([cat, val]) => {
        const catScore = val && typeof val === "object" ? val.avgScore : val;
        if (catScore == null) return;
        if (!catAgg[cat]) catAgg[cat] = { sum: 0, weight: 0, subs: {} };
        catAgg[cat].sum += catScore * a.count;
        catAgg[cat].weight += a.count;
        const subAvgs = (val && typeof val === "object" && val.subcategoryAvgs) || {};
        Object.entries(subAvgs).forEach(([sub, subScore]) => {
          if (subScore == null) return;
          if (!catAgg[cat].subs[sub]) catAgg[cat].subs[sub] = { sum: 0, weight: 0 };
          catAgg[cat].subs[sub].sum += subScore * a.count;
          catAgg[cat].subs[sub].weight += a.count;
        });
      });
    });
    const categoryAvgs = {};
    Object.entries(catAgg).forEach(([cat, agg]) => {
      const subcategoryAvgs = {};
      Object.entries(agg.subs).forEach(([sub, s]) => {
        subcategoryAvgs[sub] = Math.round((s.sum / s.weight) * 10) / 10;
      });
      categoryAvgs[cat] = { avgScore: Math.round((agg.sum / agg.weight) * 10) / 10, subcategoryAvgs };
    });

    return { count: totalCount, avgScore, categoryAvgs };
  };

  return {
    list,
    prevMonth: buildAggregate("prevMonth"),
    currMonth: buildAggregate("currMonth"),
  };
}

export function applyMockNameMasking(rawData) {
  if (!rawData || !rawData.brands) return rawData;
  const cloned = JSON.parse(JSON.stringify(rawData));
  const { nameToPlaceholder, brandKeyMap } = buildMockNameMap(cloned);

  const renamedBrands = {};
  Object.keys(cloned.brands).forEach((brandKey) => {
    renamedBrands[brandKeyMap[brandKey]] = cloned.brands[brandKey];
  });
  cloned.brands = renamedBrands;

  const regex = buildMockNameRegex(Object.keys(nameToPlaceholder));
  return maskStringsDeep(cloned, regex, nameToPlaceholder);
}

// ── OTA 渠道(lastMonthSourceStats)共用逻辑 ──
// 品牌级 "Hotel performance & local benchmarks" 的 OTA 模式(BrandLevel.vue)和
// 一级页 "Property performance" 的渠道列(PropertyPerformanceTable.vue)读的是同
// 一份 geo.lastMonthSourceStats,列的取舍/排序/取图规则必须一致,所以放在这里。

// 渠道列的相对顺序,和 competitors 页 "Average score of OTA platforms" 保持一致。
// 只定顺序,不是白名单 —— 不在这里的渠道(接口以后新增)仍会显示,排在已知渠道
// 之后按字母序,不会被丢掉。
export const OTA_SOURCE_ORDER = [
  "BOOKING", "TRIP", "EXPEDIA", "GOOGLE", "TRIPADVISOR", "AGODA",
  "HOLIDAYCHECK", "TRAVELOKA", "HOTELSCOM", "HRS", "TRIVAGO", "PRICELINE",
  "LASTMINUTE", "RAKUTEN", "HOSTELWORLD", "TRAVELOCITY", "ORBITZ",
  "LUXURYESCAPES", "INTERNAL", "MAKEMYTRIP", "HOTELDE",
];

// 一批酒店里实际出现过的渠道的并集,按 OTA_SOURCE_ORDER 排好。
// 写死一套固定列会给没接的渠道留一整片空列 —— 各家酒店接入的 OTA 不一样。
// rows: [{ otaStats: [{source, reviewCount, avgScore}] }]
export function collectOtaSources(rows) {
  const seen = new Set();
  (rows || []).forEach((r) => {
    ((r && r.otaStats) || []).forEach((s) => { if (s && s.source) seen.add(s.source); });
  });
  const known = OTA_SOURCE_ORDER.filter((s) => seen.has(s));
  const rest = [...seen].filter((s) => OTA_SOURCE_ORDER.indexOf(s) === -1).sort();
  return [...known, ...rest];
}

export function otaStatFor(row, source) {
  return ((row && row.otaStats) || []).find((s) => s && s.source === source) || null;
}

export function otaTotalReviews(row) {
  return ((row && row.otaStats) || []).reduce((sum, s) => sum + ((s && s.reviewCount) || 0), 0);
}

// 各渠道分制不一样(Google/Tripadvisor 是 5 分制,Booking 等是 10 分制),接口给
// 什么就显示什么,不做换算 —— 和 competitors 那张表的处理一致。
export function formatOtaScore(value) {
  return value == null ? "—" : String(value);
}

// 和 PlatformPickerHome/ResponseRate 同一套:图标按渠道名小写取,缺图回落 default。
// store 是可选的:传了的话 INTERNAL 会按酒店所属集团换标(见 utils/otaLogo.js);
// 不传就还是 internal.png。调用方在组件里就传 this.$store。
export function otaPlatformIcon(source, store) {
  if (store && String(source || "").toUpperCase() === "INTERNAL") {
    return internalLogo(store);
  }
  try {
    return require(`@/assets/platforms/${String(source || "").toLowerCase()}.png`);
  } catch (e) {
    return require("@/assets/platforms/default.png");
  }
}

// Reference-counted page-scroll lock for the "flagged for discussion"-style
// el-drawers (PortfolioLevel/BrandLevel's discuss/driver-signal/hotel-flags
// drawers) (2026-09). el-drawer's own `lock-scroll` prop only toggles
// `overflow: hidden` on document.body, which alone doesn't stop scrolling
// here for two separate reasons:
//   1. This app's own main-content scroll happens on <html>, not <body> (see
//      layout/components/AppMain.vue: `html { overflow-y: scroll }`, kept
//      always-visible on purpose to reserve gutter space) — locking body is
//      simply a no-op for that.
//   2. The sidebar's own nav list (layout/components/Sidebar/RouterMenu.vue's
//      .RouterMenuBox) scrolls in a COMPLETELY SEPARATE container with its
//      own independent overflow-y: auto — no amount of locking <html> or
//      <body> reaches it, so it kept scrolling underneath the drawer too.
// Rather than hunting down and individually locking every independently-
// scrollable region in the app (fragile — the next one added would just be
// another gap), this blocks the scroll GESTURE itself: a capturing wheel/
// touchmove listener on window preempts the event everywhere EXCEPT inside
// the currently-open drawer's own DOM (matched via closest(DRAWER_SELECTOR)),
// so the drawer's own content can still scroll internally while nothing
// behind it — main content, sidebar, or any other region — can.
// <html>/<body> overflow is still set too (belt-and-suspenders: stops
// scrollbar-drag and keyboard PageDown/Space/arrow-key scrolling of the main
// page, which the wheel/touch listener alone wouldn't catch), with the
// removed scrollbar's width compensated via padding-right so the page
// content doesn't visibly shift/reflow when the scrollbar disappears.
// Reference-counted so two drawers toggling independently (a component can
// own more than one) can't have one drawer's close prematurely unlock scroll
// while the other is still open.
// Element UI's own class on the actual sliding panel element, NOT
// ".bo2-discuss-drawer" — a `class` attribute passed to <el-drawer> merges
// onto its root, which is the full-viewport `.el-drawer__wrapper` (see
// element-ui's drawer/src/main.vue), and that wrapper's own dimmed backdrop
// sits INSIDE it too. Matching on that would have exempted the backdrop from
// the block along with the real panel, letting wheel events over the dimmed
// background still fall through to whatever's behind it. ".el-drawer" is
// the inner panel div itself, present on every drawer regardless of any
// custom class, so scoping to it excludes the backdrop correctly.
// 锁住背景时仍允许滚动的区域：Element 抽屉、自己做的底部弹层（加 data-scroll-allow），
// 以及挂在 body 上的下拉 / 日期面板
const DRAWER_SELECTOR = ".el-drawer, [data-scroll-allow], .el-select-dropdown, .el-picker-panel";
let openDrawerCount = 0;
function preventScrollOutsideDrawer(e) {
  if (e.target && e.target.closest && e.target.closest(DRAWER_SELECTOR)) return;
  e.preventDefault();
}
export function lockPageScroll() {
  openDrawerCount += 1;
  if (openDrawerCount !== 1) return;
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.documentElement.style.overflow = "hidden";
  document.body.style.overflow = "hidden";
  if (scrollbarWidth > 0) document.documentElement.style.paddingRight = `${scrollbarWidth}px`;
  window.addEventListener("wheel", preventScrollOutsideDrawer, { passive: false });
  window.addEventListener("touchmove", preventScrollOutsideDrawer, { passive: false });
}
export function unlockPageScroll() {
  openDrawerCount = Math.max(0, openDrawerCount - 1);
  if (openDrawerCount !== 0) return;
  document.documentElement.style.overflow = "";
  document.body.style.overflow = "";
  document.documentElement.style.paddingRight = "";
  window.removeEventListener("wheel", preventScrollOutsideDrawer);
  window.removeEventListener("touchmove", preventScrollOutsideDrawer);
}

// 酒店名只缩写品牌前缀（2026-09-30）：InterContinental → IC，Vignette Collection → VC，其余原样。
// 用在列表/悬停提示这类放不下全称的地方，调用方自己决定哪里用、哪里保留全称。
const HOTEL_BRAND_ABBREVIATIONS = [
  { pattern: /^intercontinental\b/i, short: "IC" },
  { pattern: /^vignette collection\b/i, short: "VC" },
];
export function abbreviateHotelName(name) {
  const text = String(name || "");
  const hit = HOTEL_BRAND_ABBREVIATIONS.find((a) => a.pattern.test(text));
  return hit ? text.replace(hit.pattern, hit.short) : text;
}

// 一组酒店名共有的开头几个词（按词比，至少要留下一个词），如 ["The St. Regis Beijing", "The St. Regis Macao"]
// → "The St. Regis "；少于两家时返回 ""。品牌下的酒店名去掉它就是城市 / 地段名。
export function commonNamePrefix(names) {
  const list = (names || []).map((n) => String(n || "")).filter(Boolean);
  if (list.length < 2) return "";
  const words = list.map((n) => n.split(/\s+/));
  const common = [];
  for (let i = 0; i < words[0].length - 1; i++) {
    if (words.every((w) => w[i] === words[0][i] && w.length > i + 1)) common.push(words[0][i]);
    else break;
  }
  return common.length ? common.join(" ") + " " : "";
}

// 酒店名去掉品牌部分，按顺序：
//   1. 有 ", a / an …"（"Rissai Valley, a Ritz-Carlton Reserve" / "Londoner Grand, a Luxury Collection Hotel, Macao"）→ 取它前面；
//   2. 以本品牌共有的开头起头 → 去掉它（"The St. Regis on the Bund, Shanghai" → "on the Bund, Shanghai"）；
//   3. 带逗号 → 取最后一段（"The Ritz-Carlton, Harbin" → "Harbin"）。
// 去掉后开头的 "Hotel" 也去掉（"JW Marriott Hotel Taiyuan" → "Taiyuan"）；剩下不到 3 个字符时（打码名）用全名
export function hotelNameWithoutBrand(name, prefix) {
  const full = String(name || "");
  const comma = full.lastIndexOf(", ");
  const article = full.search(/,\s+an?\s/i);
  let short = "";
  if (article !== -1) short = full.slice(0, article);
  else if (prefix && full.startsWith(prefix)) short = full.slice(prefix.length);
  else if (comma !== -1) short = full.slice(comma + 2);
  short = short.replace(/^Hotel\s+/i, "").trim();
  return short.length > 2 ? short : full;
}

// 品牌代码（一级页 All properties 的品牌标签用，悬停看全名）：万豪的按集团惯用代码，
// 其他品牌取首字母（两个词以上取前两个词的首字母，一个词取前两个字母）
const BRAND_CODES = {
  "jw marriott": "JW",
  "the ritz-carlton": "RC",
  "ritz-carlton": "RC",
  "st. regis": "XR",
  "w hotels": "WH",
  "the luxury collection": "LC",
  "edition hotels": "EB",
  "edition": "EB",
  "bvlgari": "BG",
};
export function brandCode(name) {
  const text = String(name || "").trim();
  const known = BRAND_CODES[text.toLowerCase()];
  if (known) return known;
  const words = text.replace(/^the\s+/i, "").split(/[\s-]+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return (words[0] || "").slice(0, 2).toUpperCase();
}

// 从酒店名里认城市（2026-11-04，地图标签起用；品牌页酒店表的「RC Shanghai」也用它）：接口没有城市字段，从酒店名里认。"The Portman Ritz-Carlton Shanghai" → "Shanghai"，
// "The Ritz-Carlton, Sanya, Yalong Bay" → "Sanya"。名字里出现多个时取最靠前的；认不出（"Rissai Valley, a Ritz-Carlton
// Reserve"）退回上面去品牌前缀的短名。名单按万豪中国 / IHG 泰国现有酒店整理，新城市往这里加。
const CITY_NAMES = [
  "Beijing", "Shanghai", "Guangzhou", "Shenzhen", "Chengdu", "Chongqing", "Hangzhou", "Nanjing", "Suzhou", "Tianjin",
  "Wuhan", "Changsha", "Harbin", "Xi'an", "Taiyuan", "Zhengzhou", "Qingdao", "Dalian", "Xiamen", "Zhuhai", "Sanya",
  "Haikou", "Nanning", "Kunming", "Dali", "Lhasa", "Yinchuan", "Qufu", "Anji", "Hong Kong", "Macau",
  "Taipei", "Kaohsiung",
  "Bangkok", "Phuket", "Chiang Mai", "Khao Yai", "Koh Samui", "Samui", "Pattaya", "Hua Hin", "Krabi", "Yao Noi", "Ko Chang",
];
// 名字里的其他写法 → 显示的城市名。Rissai Valley（Ritz-Carlton Reserve）名字里没有城市，在九寨沟
const CITY_ALIASES = { xian: "Xi'an", macao: "Macau", "rissai valley": "Jiuzhaigou" };
const CITY_PATTERN = new RegExp(
  `\\b(${CITY_NAMES.concat(Object.keys(CITY_ALIASES)).map((c) => c.replace(/'/g, "'?").replace(/\s+/g, "\\s+")).join("|")})\\b`,
  "gi",
);
export function cityOfHotel(name) {
  const text = String(name || "");
  let first = null;
  for (const m of text.matchAll(CITY_PATTERN)) {
    if (!first || m.index < first.index) first = m;
  }
  if (!first) return "";
  const raw = first[1].replace(/\s+/g, " ");
  const alias = CITY_ALIASES[raw.toLowerCase().replace(/'/g, "")];
  if (alias) return alias;
  return CITY_NAMES.find((c) => c.toLowerCase().replace(/'/g, "") === raw.toLowerCase().replace(/'/g, "")) || raw;
}

// 品牌页酒店表的短名：品牌代码 + 城市（"The Portman Ritz-Carlton Shanghai" → "RC Shanghai"，2026-11-05）。
// 同一品牌同一城市不止一家时，后面补上名字里剩下的词区分（"RC Shanghai Portman" / "RC Shanghai Pudong"）。
// 认不出城市返回 ""，调用方退回原来的名字。names：这个品牌所有酒店名，用来判断重名
const NAME_FILLER_WORDS = new Set(["the", "a", "an", "at", "on", "by", "hotel", "hotels", "resort", "resorts", "spa", "&", "and", "reserve", "collection", "luxury", "-"]);
export function brandCityName(name, brandName, names = []) {
  const city = cityOfHotel(name);
  if (!city) return "";
  const base = `${brandCode(brandName)} ${city}`;
  const sameCity = names.filter((n) => cityOfHotel(n) === city);
  if (sameCity.length < 2) return base;
  const skip = new Set(
    String(brandName || "").toLowerCase().split(/[\s,]+/)
      .concat(city.toLowerCase().split(/\s+/), ["marriott", "ritz-carlton", "jw", "w", "st.", "regis", "edition", "bvlgari"]),
  );
  const rest = String(name).replace(/,/g, " ").split(/\s+/)
    .filter((w) => w && !skip.has(w.toLowerCase()) && !NAME_FILLER_WORDS.has(w.toLowerCase()) && w.toLowerCase().replace(/'/g, "") !== city.toLowerCase().replace(/'/g, ""))
    .join(" ");
  return rest ? `${base} ${rest}` : base;
}
